import type { GridColumnProps } from '@progress/kendo-react-grid';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';

import { ClientError } from '@ricoh/ria.client.core/errors';
import { createLogger } from '@ricoh/ria.client.core/plugins/app/logger';
import { isNotNull, isNull } from '@ricoh/ria.client.core/utils/data/object';

import type { DataType, DataValue, DataValueGeneral, DataValueList, DataValueNull, DataValueTable, DataValueTableRow } from '~/common/model/api/common';

import { DATA_TYPE_GENERAL_NAMES } from '~/common/constants/feature/settings/batch-type-library';
import commonLogic from '~/common/logic/common';

import type { DataTypeFormField } from './_type.type';
import type {
  DataValueFormField,
  DataValueFormFieldGeneral,
  DataValueFormFieldList,
  DataValueFormFieldTableItem,
  DataValueFormFieldTableWithColumns,
} from './_value.type';

import { createDataType, createDataTypeFormField } from './_type';
import { isDataTypeFormField, isDataValue } from './_utils';

const logger = createLogger('logic/feature/settings/batch-type-library');

/**
 * Create General FieldValue or List FieldValue from DataValue FormField.
 *
 * @param {DataType} fieldType FieldType.
 * @param {DataValueFormField} dataValueFormField DataValue FormField..
 *
 * @returns {DataValueGeneral | DataValueList | undefined} General FieldValue or List FieldValue.
 */
function createGeneralOrListFieldValue(fieldType: DataType, dataValueFormField: DataValueFormField): DataValueGeneral | DataValueList | undefined {
  if (DATA_TYPE_GENERAL_NAMES.includes(fieldType.name)) {
    return {
      type: fieldType.name,
      value: dataValueFormField,
    } as DataValueGeneral;
  }

  const listFieldType = commonLogic.type.getListDataType(fieldType.name);
  if (listFieldType) {
    return {
      type: fieldType.name,
      value: [dataValueFormField],
    } as DataValueList;
  }

  return undefined;
}

/**
 * Create FieldValue.
 *
 * @param {DataType} fieldType FieldType.
 * @param {DataValueFormField} dataValueFormField DataValue FormField..
 *
 * @returns {DataValue | DataValueNull} FieldValue.
 */
export function createDataValue(fieldType: DataType, dataValueFormField: DataValueFormField): DataValue | DataValueNull {
  if (isNull(dataValueFormField)) {
    return {};
  }

  const result = createGeneralOrListFieldValue(fieldType, dataValueFormField);
  if (result) {
    return result;
  }

  if (fieldType.name === 'table') {
    return {
      type: fieldType.name,
      value: (Array.isArray(dataValueFormField) ? dataValueFormField : []).map<DataValueTableRow>(
        (item) =>
          fieldType.columns?.reduce<DataValueTableRow>(
            (prev, column, index) => ({
              ...prev,
              [index]: createGeneralOrListFieldValue(column.type, item[column.name]) ?? {},
            }),
            {},
          ) ?? {},
      ),
    } as DataValueTable;
  }

  throw new ClientError({
    module: 'logic',
    message: 'Invalid data type',
    cause: fieldType,
  });
}

/**
 * Create General DataValue or List DataValue FormField.
 *
 * @param {DataValue} fieldValue Value.
 * @param {DataTypeFormField | undefined} dataTypeFormField DataType FormField.
 *
 * @returns {DataValueFormFieldGeneral | DataValueFormFieldList | undefined} General DataValue or List DataValue FormField.
 */
function createGeneralOrListDataValueFormField(
  fieldValue: DataValue,
  dataTypeFormField?: DataTypeFormField,
): DataValueFormFieldGeneral | DataValueFormFieldList | undefined {
  // * Strict type
  if (dataTypeFormField) {
    if (isDataTypeFormField.list(dataTypeFormField) && Array.isArray(fieldValue.value)) {
      return fieldValue.value.at(0) as DataValueFormFieldList;
    }

    if (isDataTypeFormField.general(dataTypeFormField)) {
      return fieldValue.value as DataValueFormFieldGeneral;
    }
  }
  // * Not strict type
  else {
    if (isDataValue.list(fieldValue) && Array.isArray(fieldValue.value)) {
      return fieldValue.value.at(0);
    }

    if (isDataValue.general(fieldValue)) {
      return fieldValue.value;
    }
  }

  return undefined;
}

/**
 * Create DataValue FormField.
 *
 * @template TStrict Strict type.
 * @param {Nullable<DataValue | DataValueNull>} fieldValue Value.
 * @param {DataType | undefined} fieldType FieldType.
 *
 * @returns {DataValueFormField<DataType | undefined>} DataValue FormField.
 */
export function createDataValueFormField<TStrict extends boolean = true>(
  fieldValue: Nullable<DataValue | DataValueNull>,
  fieldType?: TStrict extends true ? DataType : undefined,
): DataValueFormField<TStrict extends true ? DataType : undefined> {
  if (!fieldValue?.type) {
    return null;
  }

  // * Strict type
  if (fieldType) {
    if (fieldType.name !== fieldValue.type) {
      logger.writeWithData('warn', 'Not match field type and value type', {
        type: fieldType,
        value: fieldValue,
      });

      return null;
    }

    const dataTypeFormField = createDataTypeFormField(fieldType);
    const result = createGeneralOrListDataValueFormField(fieldValue, dataTypeFormField);
    if (isNotNull(result)) {
      return result;
    }

    if (isDataTypeFormField.table(dataTypeFormField) && Array.isArray(fieldValue.value)) {
      return fieldValue.value.map<DataValueFormFieldTableItem>((item) => {
        if (isNull(item) || typeof item !== 'object') {
          return {};
        }

        return dataTypeFormField.columns.reduce((prev, column, index) => {
          const cellFieldValue = item[index];
          if (!cellFieldValue?.type) {
            return {
              ...prev,
              [column.name]: null,
            };
          }

          const cellFieldType = createDataType(column);
          if (cellFieldType.name !== cellFieldValue.type) {
            logger.writeWithData('warn', 'Not match cell field type and cell value type', {
              type: cellFieldType,
              value: cellFieldValue,
            });

            return {
              ...prev,
              [column.name]: null,
            };
          }

          const cellValue = createGeneralOrListDataValueFormField(cellFieldValue, column);
          if (isNotNull(cellValue)) {
            return {
              ...prev,
              [column.name]: cellValue,
            };
          }

          return prev;
        }, {});
      }) as DataValueFormField<TStrict extends true ? DataType : undefined>;
    }
  }
  // * Not strict type
  else {
    const result = createGeneralOrListDataValueFormField(fieldValue);
    if (isNotNull(result)) {
      return result;
    }

    if (isDataValue.table(fieldValue) && Array.isArray(fieldValue.value) && fieldValue.value.length > 0) {
      return fieldValue.value.reduce<DataValueFormFieldTableWithColumns>(
        (prev, item, index) => {
          if (index === 0) {
            return {
              ...prev,
              columns: Object.entries(item).map<GridColumnProps>(([key, headerValue]) => ({
                field: key,
                title: headerValue.type === 'text' ? (headerValue.value ?? undefined) : undefined,
              })),
            };
          }

          prev.dataItem.push(
            Object.entries(item).reduce<DataValueFormFieldTableItem>((prevDataItem, [key, cellValue]) => {
              if (!cellValue?.type) {
                return {
                  ...prevDataItem,
                  [key]: null,
                };
              }

              const value = createGeneralOrListDataValueFormField(cellValue);
              if (isNotNull(value)) {
                return {
                  ...prevDataItem,
                  [key]: value,
                };
              }

              return prevDataItem;
            }, {}),
          );

          return prev;
        },
        {
          columns: [],
          dataItem: [],
        },
      ) as DataValueFormField<TStrict extends true ? DataType : undefined>;
    }
  }

  return null;
}
